<template>
  <!-- Invoice lines table -->
  <table class="gk-table gk-table-invoicelines gk-table-invoicelines--preview gk-table--responsive">

    <!-- Table head -->
    <thead>
      <td v-if="!excludeTableFields.includes('quantity')" class="text-center">{{ $gettext('Quantity') }}</td>
      <td v-if="!excludeTableFields.includes('description')">{{ $gettext('Description') }}</td>
      <td class="">
        <span v-if="invoice.line_amount_is_gross">{{ $gettext('Unit price (Inc. VAT)') }}</span>
        <span v-else>{{ $gettext('Unit price (Ex. VAT)') }}</span>
      </td>
      <td class="text-right">
        <span v-if="invoice.line_amount_is_gross">{{ $gettext('Total (Inc. VAT)') }}</span>
        <span v-else>{{ $gettext('Total (Ex. VAT)') }}</span>
      </td>
      <td class="text-right">{{ $gettext('Tax Rate') }}</td>
    </thead>

    <!-- Table body lines table -->
    <tbody>
      <tr v-for="(invoice_line, invoice_index) in invoice_lines" :key="'invoice_line-' + invoice_index">

        <!-- Quantity -->
        <td v-if="!excludeTableFields.includes('quantity')" class="col-quantity text-center">
          {{ invoice_lines[invoice_index].multiplier }}
        </td>

        <!-- Description -->
        <td v-if="!excludeTableFields.includes('description')" class="col-desc">
          {{ invoice_lines[invoice_index].description }}
        </td>

        <!-- Unit price -->
        <td class="col-price">
          <span v-html="currencyByCode(invoice.currency).symbol" />
          {{ invoice_lines[invoice_index].amount }}
        </td>

        <!-- Sub/Total (Ex. VAT) -->
        <td class="text-right col-subtotal">
          <span><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ invoice_line.subtotal_amount | formatNumber }}</span>
        </td>

        <!-- Tax Rate -->
        <td class="col-vat text-right">
          <div v-if="!invoice.vat_reversed_charge">
            {{ getVatInputRate(invoice_index) }}
          </div>
          <div v-else class="vat-reversed">
            <!-- <font-awesome-icon icon="fa-solid fa-minus" /> -->
            {{ $gettext('VAT Reverse charge') }}
          </div>
        </td>

      </tr>
    </tbody>

    <!-- Table footer Totals and vat lines -->
    <tfoot>

      <!-- Subtotal -->
      <tr>
        <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
        <td class="text-right">{{ $gettext('Subtotal') }}</td>
        <td class="text-right"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ subtotal | formatNumber }}</td>
        <td colspan="2"></td>
      </tr>

      <!-- Vat lines -->
      <template v-for="(vat_amount, vat_index, index) in vatTotals" >
        <tr v-if="!invoice.vat_reversed_charge" :key="'vat_totals-' + vat_index">
          <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
          <td class="text-right">{{ vat_index }}% <span>{{ $gettext('VAT') }}</span></td>
          <td class="text-right"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ vat_amount | formatNumber }}</td>
          <td v-if="index === 0" :rowspan="Object.keys(vatTotals).length" colspan="2" class="vat-reverser">
          </td>
        </tr>
      </template>
      
      <tr v-if="invoice.vat_reversed_charge">
        <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
        <td class="text-right">{{ $gettext('VAT') }}</td>
        <td class="text-right"> <font-awesome-icon icon="fa-solid fa-minus" /></td>
        <td colspan="2" class="vat-reverser reversed">
          <span>{{ $gettext('Reversed charge') }}</span>
        </td>
      </tr>

      <!-- IRPF (spain) -->
      <tr v-if="currentCompany.country === 'ES' && (selectedCustomer && selectedCustomer.country === 'ES')" class="vat-line">
        <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
        <td class="text-right">
          <div class="d-flex justify-content-end align-items-center" style="gap: 0.5rem;">
            {{ $gettext('IRPF') }}
            <div>{{ invoice.retention_tax_percentage }}</div>
          </div>
        </td>
        <td id="subtotal" class="text-right">
          <span v-html="currencyByCode(invoice.currency).symbol"></span>{{ invoice.retention_tax_amount | formatNumber }}
        </td>
      </tr>

      <!-- Separator total -->
      <tr class="total-tr-separator">
        <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
        <td colspan="2" class="separator">
          <hr>
        </td>
        <td></td>
        <td></td>
      </tr>

      <!-- Total -->
      <tr class="total-tr">
        <td v-if="subtotalPreColspan > 0" :colspan="subtotalPreColspan"></td>
        <td class="text-right total-total">{{ $gettext('Total') }}</td>
        <td class="text-right total-total"><span v-html="currencyByCode(invoice.currency).symbol"></span>{{ total | formatNumber }}</td>
        <td colspan="2" class="total-total"></td>
      </tr>

    </tfoot>
  </table>
</template>

<script>
import { mapGetters } from "vuex"

export default {
  name: 'InvoiceLinesPreview',
  props: {
    invoice: {
      type: Object,
      required: true
    },
    excludeTableFields: {
      type: Array,
      default: () => []
    },
  },
  data () {
    return {
    }
  },
  computed: {
    ...mapGetters({ 
      currentCompany: "main/getCurrentCompany",
      currencyByCode: "main/getCurrencyByCode",
      selectedCustomer: "invoices/getInvoiceCreateRecipient",
      ueCountryCodes: "main/getUeCountryCodes",
    }),
    invoice_lines () {
      return this.invoice.invoice_lines
    },
    subtotalPreColspan () {
      let colspan = 2
      if (this.excludeTableFields.includes('quantity')) {
        colspan--
      }
      if (this.excludeTableFields.includes('description')) {
        colspan--
      }
      return colspan
    },
    subtotal () {
      let subTotal = 0
      this.invoice_lines.forEach(invoice_line => {
        if (this.invoice.line_amount_is_gross) {
          if (invoice_line.vat_exempt || this.invoice.vat_reversed_charge) {
            subTotal += invoice_line.multiplier * invoice_line.amount
          }else{
            subTotal += invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
          }
        } else {
          subTotal += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        }
      })
      return subTotal
    },
    vatTotals () {
      const vat_totals = {}
      this.invoice_lines.forEach(invoice_line => {
        if((invoice_line.amount > 0 || invoice_line.amount < 0) && !invoice_line.vat_exempt) {
          if (!vat_totals[invoice_line.tax_rate]) {
            vat_totals[invoice_line.tax_rate] = 0
          }
          if (this.invoice.line_amount_is_gross) {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100 - Math.round((invoice_line.multiplier * invoice_line.amount / ( 1 + invoice_line.tax_rate / 100)) * 100) / 100
          } else {
            vat_totals[invoice_line.tax_rate] += Math.round(invoice_line.multiplier * invoice_line.amount * (invoice_line.tax_rate / 100) * 100) / 100
          }
        }
      })
      return vat_totals
    },
    total () {
      let total = 0
      if (this.invoice.line_amount_is_gross) {
        this.invoice_lines.forEach(invoice_line => {
          total += Math.round(invoice_line.multiplier * invoice_line.amount * 100) / 100
        })
      } else {
        const subtotal = Math.round(parseFloat(this.subtotal) * 100) / 100
        let total_vat = 0

        for (let vat in this.vatTotals) {
          total_vat += Math.round(this.vatTotals[vat] * 100) / 100
        }
        
        if (this.invoice.vat_reversed_charge) total_vat = 0

        total = subtotal + total_vat
      }
      this.calculateRetention()
      if (this.invoice.retention_tax_amount) total += this.invoice.retention_tax_amount

      return total
    }
  },
  created () {
    this.calculateInvoiceLines()
  },
  methods: {
    /**
     * Calculates subtotal_amount for the given invoice_line based on multiplier and amount
     * 
     * @param {number} invoice_index index from invoice_lines array
     */
    computeNetValue (invoice_index) {
      let invoice_line = this.invoice_lines[invoice_index]

      if(!this.invoice.vat_reversed_charge){
        if (invoice_line.vat_exempt || invoice_line.tax_rate === 'exempt') {
          invoice_line.tax_rate = 0
        }
        // invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount / (1 + invoice_line.tax_rate / 100)
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      } else {
        invoice_line.subtotal_amount = invoice_line.multiplier * invoice_line.amount
      }
      // invoice_line = Math.round(invoice_line * 100) / 100; //make sure we have only 2 decimals
    },
    /**
     * Calculates NetValue for all items on this.invoice_lines
     */
    calculateInvoiceLines () {
      for (var i = 0; i < this.invoice_lines.length; i++) {
        // calculate only non default lines
        if (!this.invoice_lines[i].default) {
          this.computeNetValue(i)
        }
      }
    },
    /**
     * Sets invoice retention_tax_amount and retention_tax_percentage for spanish companies
     * 
     * @param {number} retention_percentage retention amount
     */
    calculateRetention (retention_percentage) {
      if (this.currentCompany.country !== 'ES' || (this.selectedCustomer && this.selectedCustomer.country !== 'ES')) {
        this.$set(this.invoice, 'retention_tax_amount', null)
        this.$set(this.invoice, 'retention_tax_percentage', null)
        return
      }
      if (retention_percentage) {
        this.$set(this.invoice, 'retention_tax_percentage', retention_percentage)
      }

      let retention_amount = -Math.round(this.subtotal * this.invoice.retention_tax_percentage) / 100
        this.$set(this.invoice, 'retention_tax_amount', retention_amount)

      if (!this.invoice.retention_tax_amount) {
        this.$set(this.invoice, 'retention_tax_amount', 0)
      }
    },
    getVatInputRate (invoice_index) {
      let tax_rate = this.invoice_lines[invoice_index].tax_rate + '%'
      if (this.invoice_lines[invoice_index].vat_exempt) {
        tax_rate = 'exempt'
      }
      return tax_rate
    }
  }
}
</script>

<style lang="scss">
  .gk-table-invoicelines {
    &--preview {
      .col-desc { min-width: 150px; width: auto !important; }
      .col-price { min-width: 100px; width: auto !important; }
      .col-subtotal { min-width: 100px; width: auto !important; }
      .col-vat { 
        min-width: 120px; 
        width: auto !important; 
        text-align: center;
        .vat-reversed { color: $colorUnassigned; }
      }
    }
  }
</style>
